import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  amenityFallbackLabel,
  amenityIconUrl,
  amenityLabelKey,
  hasHiddenAmenities,
  propertyMaxOccupancy,
  validAmenities,
  visibleAmenities,
} from "../../helper/amenities";

export const AmenityIcon = ({ icon }) => {
  const url = `url("${amenityIconUrl(icon)}")`;

  return (
    <span
      className='amenity-icon'
      style={{ maskImage: url, WebkitMaskImage: url }}
      aria-hidden='true'
    />
  );
};

// A few pictograms with tooltips for the property cards and map popups.
export const AmenityIconList = ({ amenities, limit = 6 }) => {
  const { t } = useTranslation();
  const items = validAmenities(amenities).slice(0, limit);

  if (items.length === 0) {
    return null;
  }

  return (
    <ul className='featureIcnList listUnstyled'>
      {items.map((amenity) => {
        const label = t(amenityLabelKey(amenity.code), {
          defaultValue: amenityFallbackLabel(amenity.code),
        });

        return (
          <li key={amenity.code}>
            <span
              className='Icn'
              data-tooltip-id='my-tooltip'
              data-tooltip-content={label}
              data-tooltip-place='top'
              aria-label={label}
            >
              <AmenityIcon icon={amenity.icon} />
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// Capacity and a few amenity pictograms instead of the description on property cards.
export const PropertyCardSummary = ({ property, limit = 5 }) => {
  const { t } = useTranslation();
  const maxOccupancy = propertyMaxOccupancy(property);
  const hasAmenities = validAmenities(property?.amenities).length > 0;

  if (!maxOccupancy && !hasAmenities) {
    return null;
  }

  return (
    <div className='property-card-summary'>
      {maxOccupancy && (
        <div className='property-card-summary__capacity'>
          <AmenityIcon icon='groups' />
          <span>
            {t("max_occupancy_guests", {
              count: maxOccupancy,
              defaultValue: "Max. occupancy {{count}}",
            })}
          </span>
        </div>
      )}
      <AmenityIconList amenities={property?.amenities} limit={limit} />
    </div>
  );
};

// Property or apartment amenities in two columns, the rest behind "Show all".
const PropertyAmenities = ({
  amenities,
  title,
  maxOccupancy = null,
  limit = 10,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const items = validAmenities(amenities);
  if (items.length === 0) {
    return null;
  }

  const shown = visibleAmenities(items, expanded, limit);

  return (
    <section className={`property-amenities ${compact ? "property-amenities--compact" : ""}`}>
      {title && <h3 className='property-amenities__title'>{title}</h3>}
      <ul className='property-amenities__grid listUnstyled'>
        {maxOccupancy && (
          <li className='property-amenities__item'>
            <AmenityIcon icon='groups' />
            <span>
              {t("max_occupancy_guests", {
                count: maxOccupancy,
                defaultValue: "Max. occupancy {{count}}",
              })}
            </span>
          </li>
        )}
        {shown.map((amenity) => (
          <li key={amenity.code} className='property-amenities__item'>
            <AmenityIcon icon={amenity.icon} />
            <span>
              {t(amenityLabelKey(amenity.code), {
                defaultValue: amenityFallbackLabel(amenity.code),
              })}
            </span>
          </li>
        ))}
      </ul>
      {hasHiddenAmenities(items, limit) && (
        <button
          type='button'
          className='property-amenities__toggle'
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? t("show_less_amenities", "Show less")
            : t("show_all_amenities", {
                count: items.length,
                defaultValue: "Show all amenities ({{count}})",
              })}
        </button>
      )}
    </section>
  );
};

export default PropertyAmenities;
